import React, { useState } from 'react';
import { Form, Button, Table, Alert, Spinner } from 'react-bootstrap';
import { crearDatos } from '../../../api/common';
import ItemSearchSelect from '../../../utils/ItemSearchSelect';

/**
 * EquipoManual - Component for manual equipment entry (table format)
 * @param {Object} props - Component props
 * @param {string} props.clienteId - ID of selected cliente
 * @param {string} props.sedeId - ID of selected sede
 * @param {Function} props.onValidarDuplicados - Handler to validate duplicates before adding
 * @param {Function} props.onAgregar - Handler receiving successfully created equipo
 * @param {Function} props.onEquiposCreados - Handler to notify when new equipos are created
 * @returns {JSX.Element}
 */
const EquipoManual = ({ clienteId, sedeId, onValidarDuplicados, onAgregar, onEquiposCreados }) => {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [form, setForm] = useState({
        item: null,
        marca: '',
        modelo: '',
        serie: '',
        inventario: '',
        ubicacion: '',
        resolucion: '',
        puntos: '',
        observacion: ''
    });

    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleItemChange = (option) => {
        setForm(prev => ({ ...prev, item: option?.data || null }));
        if (errors.item) {
            setErrors(prev => ({ ...prev, item: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!form.item) {
            newErrors.item = 'El item es requerido';
        }
        

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const clearForm = () => {
        setForm(prev => ({ ...prev, serie: '' }));
        setErrors({});
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setSubmitting(true);
        setError(null);
        setSuccess(null);

        try {
            // Step 1: Validate duplicates (solo si hay serie ingresada)
            let result = { cancelled: false, nuevosData: [], duplicadosData: [] };

            if (form.serie.trim()) {
                result = await onValidarDuplicados([form.serie.trim()]);

                if (result.cancelled) {
                    setSubmitting(false);
                    return;
                }

                // Step 2: Handle duplicados (existing equipos) - add directly
                if (result.duplicadosData.length > 0) {
                    // User selected existing equipos, add them directly (pass array at once)
                    console.log('✏️ EquipoManual: Duplicados seleccionados, llamando onAgregar:', result.duplicadosData.length);
                    onAgregar(result.duplicadosData);
                    setSuccess(`${result.duplicadosData.length} equipo(s) existente(s) agregado(s)`);
                    clearForm();
                    setTimeout(() => setSuccess(null), 3000);
                    setSubmitting(false);
                    return;
                }
            }

            // Step 3: Create new equipo (only if no duplicates or user didn't select any)
            const equipoData = {
                item: form.item._id,
                marca: form.marca,
                modelo: form.modelo,
                serie: form.serie,
                inventario: form.inventario,
                ubicacion: form.ubicacion,
                resolucion: form.resolucion,
                puntos: form.puntos,
                observacion: form.observacion,
                cliente: clienteId,
                sedeClient: sedeId
            };

            const response = await crearDatos('Equipos', equipoData);

            // Nuevo shape (spec items-adicionales-equipo-dependiente):
            //   { data: <padre>, dependientes: [<hijos>] }
            // Retro-compat: si el backend antiguo no devuelve `dependientes`, es [].
            const equipoCreado = response.data.data;
            const dependientes = response.data.dependientes || [];
            const todos = [equipoCreado, ...dependientes];

            console.log('✏️ EquipoManual: Equipo creado, llamando onAgregar con', todos.length, 'equipo(s) (padre + dependientes)');
            onAgregar(todos);

            // Notify parent to refresh EquipoExistente list
            if (onEquiposCreados) {
                onEquiposCreados();
            }

            const msgDependientes = dependientes.length > 0
                ? ` Se crearon ${dependientes.length} equipo(s) dependiente(s) automáticamente.`
                : '';
            setSuccess(`Equipo creado y agregado correctamente.${msgDependientes}`);
            clearForm();
            
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError('Error al crear equipo: ' + (err.response?.data?.message || err.message));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div>
            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}
            
            {success && (
                <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
                    {success}
                </Alert>
            )}

            <Table responsive bordered>
                <thead>
                    <tr className="table-secondary">
                        <th style={{ minWidth: '230px' }}>Item *</th>
                        <th style={{ minWidth: '130px' }}>Marca</th>
                        <th style={{ minWidth: '130px' }}>Modelo</th>
                        <th style={{ minWidth: '150px' }}>Serie</th>
                        <th style={{ minWidth: '120px' }}>Inventario</th>
                        <th style={{ minWidth: '160px' }}>Ubicación</th>
                        <th style={{ minWidth: '120px' }}>Resolución</th>
                        <th style={{ minWidth: '120px' }}>Puntos</th>
                        <th style={{ width: '60px' }}></th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <ItemSearchSelect
                                mode="async"
                                value={form.item ? {
                                    value: form.item._id,
                                    label: form.item.nombre,
                                    data: form.item
                                } : null}
                                onChange={handleItemChange}
                                placeholder="Seleccione..."
                                searchMinChars={3}
                                isClearable
                                noOptionsMessage={({ inputValue }) =>
                                    !inputValue || inputValue.trim().length < 3
                                        ? 'Escriba al menos 3 caracteres...'
                                        : 'No hay items'
                                }
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                menuPlacement="top"
                                onLoadError={(err) => {
                                    setError('Error al cargar items: ' + (err.response?.data?.message || err.message));
                                }}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        borderColor: errors.item ? '#dc3545' : base.borderColor,
                                        minHeight: '31px',
                                        height: '31px'
                                    }),
                                    valueContainer: (base) => ({
                                        ...base,
                                        height: '31px',
                                        padding: '0 6px'
                                    }),
                                    input: (base) => ({
                                        ...base,
                                        margin: '0px',
                                    }),
                                    indicatorsContainer: (base) => ({
                                        ...base,
                                        height: '31px',
                                    }),
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999
                                    })
                                }}
                            />
                        </td>
                        <td>
                            <Form.Control
                                type="text"
                                name="marca"
                                value={form.marca}
                                onChange={handleChange}
                                placeholder="Marca"
                                size="sm"
                            />
                        </td>
                        <td>
                            <Form.Control
                                type="text"
                                name="modelo"
                                value={form.modelo}
                                onChange={handleChange}
                                placeholder="Modelo"
                                size="sm"
                            />
                        </td>
                        <td>
                            <Form.Control
                                type="text"
                                name="serie"
                                value={form.serie}
                                onChange={handleChange}
                                placeholder="Serie"
                                isInvalid={!!errors.serie}
                                size="sm"
                            />
                        </td>
                        <td>
                            <Form.Control
                                type="text"
                                name="inventario"
                                value={form.inventario}
                                onChange={handleChange}
                                placeholder="Inv."
                                size="sm"
                            />
                        </td>
                        <td>
                            <Form.Control
                                type="text"
                                name="ubicacion"
                                value={form.ubicacion}
                                onChange={handleChange}
                                placeholder="Ubicación"
                                size="sm"
                            />
                        </td>
                        <td>
                            <Form.Control
                                type="text"
                                name="resolucion"
                                value={form.resolucion}
                                onChange={handleChange}
                                placeholder="Resol."
                                size="sm"
                            />
                        </td>
                        <td>
                            <Form.Control
                                type="text"
                                name="puntos"
                                value={form.puntos}
                                onChange={handleChange}
                                placeholder="Puntos"
                                size="sm"
                            />
                        </td>
                        <td className="text-center">
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? <Spinner animation="border" size="sm" /> : <i className="bi bi-plus-lg"></i>}
                            </Button>
                        </td>
                    </tr>
                </tbody>
            </Table>

            {(errors.item || errors.serie) && (
                <div className="text-danger small mb-2">
                    {errors.item && <div>• {errors.item}</div>}
                    {errors.serie && <div>• {errors.serie}</div>}
                </div>
            )}
        </div>
    );
};

export default EquipoManual;
